/* TrasparenzaPage — v1.52.0.0
 * Pagina pubblica /trasparenza ai sensi Reg. IVASS 40/2018.
 * feb-2026 hotfix: lettura DINAMICA compagnie via /api/public/compagnie-ammesse
 * (rimosse compagnie hardcoded non autorizzate — vedi memory/COMPAGNIE_ARCHIVED.md).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Building2, FileText, Users, Handshake, Scale, AlertCircle, ExternalLink, Mail } from "lucide-react";

const CARD_CLASS = "bg-white rounded-2xl border border-[#E5E2DB] p-6";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrasparenzaPage() {
  const [dirette, setDirette] = useState([]);
  const [mga, setMga]         = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/compagnie-ammesse`)
      .then(({ data }) => {
        if (cancelled) return;
        setDirette(data?.compagnie_dirette || []);
        setMga(data?.mga_grossisti || []);
      })
      .catch(() => { /* fallback vuoto - meglio nulla che dati errati */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10" data-testid="trasparenza-page">
      <header className="mb-8">
        <p className="text-[10px] uppercase tracking-widest text-[#4A5E51] font-semibold mb-2">
          Reg. IVASS 40/2018 · Trasparenza intermediario
        </p>
        <h1 className="font-heading text-3xl sm:text-5xl font-bold text-[#163321] leading-tight">
          Informazioni di Trasparenza
        </h1>
        <p className="text-sm text-[#4A5E51] mt-3 max-w-2xl">
          Documento obbligatorio ai sensi del Regolamento IVASS n. 40/2018. Descrive
          identità dell&apos;intermediario, mandanti, sistemi di remunerazione,
          conflitti di interesse e modalità di reclamo.
        </p>
      </header>

      {/* Sezione 1: Identità */}
      <section className={CARD_CLASS + " mb-6"} data-testid="section-identita">
        <h2 className="font-heading text-xl font-bold text-[#163321] flex items-center gap-2 mb-3">
          <Building2 size={22} /> Identità dell&apos;intermediario
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#4A5E51]">
          <div><strong className="text-[#163321]">Denominazione:</strong> CHRISTIAN E CIRO COLUCCI S.A.S.</div>
          <div><strong className="text-[#163321]">P.IVA / C.F.:</strong> 07618011212</div>
          <div><strong className="text-[#163321]">Sede legale:</strong> Via Armando Diaz 182/L, 80055 Portici (NA)</div>
          <div><strong className="text-[#163321]">Telefono:</strong> 081/7611000</div>
          <div><strong className="text-[#163321]">Iscrizione RUI:</strong> Sezione A n. A000464752</div>
          <div><strong className="text-[#163321]">Sedi operative:</strong> Portici · Napoli Mergellina · Pomigliano d&apos;Arco</div>
          <div><strong className="text-[#163321]">Autorità di vigilanza:</strong> IVASS</div>
          <div><strong className="text-[#163321]">PEC:</strong> colucciassicuratori@pec.it</div>
        </div>
        <div className="mt-4 pt-4 border-t border-[#E5E2DB] text-sm text-[#4A5E51]">
          <p className="font-semibold text-[#163321] mb-2">Responsabili attività di distribuzione assicurativa:</p>
          <ul className="space-y-1 text-xs">
            <li>• <strong>Christian Colucci</strong> — RUI Sez. A n. A000651816 (iscrizione 05/12/2025)</li>
            <li>• <strong>Ciro Colucci</strong> — RUI Sez. A n. A000081054 (iscrizione 19/02/2007)</li>
          </ul>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <a href="https://ruipubblico.ivass.it/rui-pubblica/ng/" target="_blank" rel="noopener noreferrer"
             className="text-xs text-[#163321] hover:underline inline-flex items-center gap-1"
             data-testid="link-rui-verifica">
            Verifica iscrizione RUI <ExternalLink size={11} />
          </a>
          <a href="https://www.ivass.it" target="_blank" rel="noopener noreferrer"
             className="text-xs text-[#163321] hover:underline inline-flex items-center gap-1">
            Sito IVASS <ExternalLink size={11} />
          </a>
        </div>
      </section>

      {/* Sezione 1-bis: Documenti Precontrattuali Ufficiali (MUP) — feb-2026 */}
      <section className={CARD_CLASS + " mb-6 border-[#163321]/30"} data-testid="section-mup">
        <h2 className="font-heading text-xl font-bold text-[#163321] flex items-center gap-2 mb-3">
          <FileText size={22} /> Documenti Precontrattuali Ufficiali (MUP)
        </h2>
        <p className="text-sm text-[#4A5E51] mb-4">
          Ai sensi del Reg. IVASS 40/2018 sono qui pubblicati i <strong>Moduli Unici Precontrattuali</strong> (MUP) dei responsabili dell&apos;attività
          di distribuzione assicurativa. Il Cliente deve prenderne visione prima della sottoscrizione di ogni polizza.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="mup-list">
          {[
            { file: "allegato_3_mup_christian_colucci.pdf", label: "Allegato 3 – Christian Colucci", sub: "MUP Modulo Unico Precontrattuale" },
            { file: "allegato_4_mup_christian_colucci.pdf", label: "Allegato 4 – Christian Colucci", sub: "MUP IBIPs (Prodotti di Investimento Assicurativi)" },
            { file: "allegato_3_mup_ciro_colucci.pdf", label: "Allegato 3 – Ciro Colucci", sub: "MUP Modulo Unico Precontrattuale" },
            { file: "allegato_4_mup_ciro_colucci.pdf", label: "Allegato 4 – Ciro Colucci", sub: "MUP IBIPs (Prodotti di Investimento Assicurativi)" },
          ].map((m) => (
            <a key={m.file}
               href={`/api/public/documenti/mup/${m.file}`}
               target="_blank"
               rel="noopener noreferrer"
               className="flex items-start gap-3 p-3 rounded-xl border border-[#E5E2DB] hover:border-[#163321] hover:bg-[#FCFCFA] transition-all"
               data-testid={`mup-link-${m.file}`}>
              <FileText size={18} className="text-[#163321] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#163321]">{m.label}</p>
                <p className="text-xs text-[#4A5E51] mt-0.5">{m.sub}</p>
              </div>
              <ExternalLink size={14} className="text-[#4A5E51] shrink-0 mt-1" />
            </a>
          ))}
        </div>
        <p className="text-[11px] text-[#4A5E51] mt-3 italic">
          I documenti sono aggiornati periodicamente. Ogni download è tracciato ai fini della conformità IVASS (prova di consegna).
        </p>
      </section>

      {/* Sezione 2: Compagnie mandanti */}
      <section className={CARD_CLASS + " mb-6"} data-testid="section-mandanti">
        <h2 className="font-heading text-xl font-bold text-[#163321] flex items-center gap-2 mb-3">
          <Handshake size={22} /> Modalità di consulenza e Compagnie mandanti
        </h2>
        <p className="text-sm text-[#4A5E51] mb-3">
          Colucci Assicuratori opera come <strong>Agente Plurimandatario</strong> in
          nome e per conto di più Compagnie convenzionate. Le proposte formulate al
          Cliente derivano da una selezione tra i prodotti disponibili delle
          Compagnie con cui è in essere un rapporto contrattuale.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#FCFCFA] text-xs uppercase tracking-wider text-[#4A5E51]">
                <th className="text-left p-2 border-b border-[#E5E2DB]">Compagnia</th>
                <th className="text-left p-2 border-b border-[#E5E2DB]">Rami principali</th>
                <th className="text-left p-2 border-b border-[#E5E2DB]">Sede legale</th>
              </tr>
            </thead>
            <tbody>
              {dirette.length === 0 && (
                <tr>
                  <td colSpan={3} className="p-2 text-[#4A5E51] italic">
                    Caricamento elenco compagnie mandanti…
                  </td>
                </tr>
              )}
              {dirette.map((c) => (
                <tr key={c.codice || c.ragione_sociale} className="hover:bg-[#FCFCFA]/50">
                  <td className="p-2 border-b border-[#E5E2DB] text-[#163321] font-medium">
                    {c.display_name || c.ragione_sociale}
                    {c.principale && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-[#8B6100] bg-[#FFF7E8] border border-[#E8D3A0] rounded-full px-1.5 py-0.5">
                        mandante principale
                      </span>
                    )}
                  </td>
                  <td className="p-2 border-b border-[#E5E2DB] text-[#4A5E51]">
                    {c.rami_autorizzati || "—"}
                  </td>
                  <td className="p-2 border-b border-[#E5E2DB] text-[#4A5E51]">
                    {c.sede || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {mga.length > 0 && (
          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-widest text-[#4A5E51] font-semibold mb-2">
              MGA / Grossisti (compagnie sottostanti eleggibili)
            </p>
            <ul className="space-y-1.5">
              {mga.map((m) => (
                <li key={m.codice || m.denominazione} className="text-sm text-[#0F1F14]" data-testid={`mga-${m.codice}`}>
                  <span className="font-medium">{m.display_name || m.denominazione}</span>
                  <span className="text-[#4A5E51]"> — {m.tipo}</span>
                  {(m.compagnie_sottostanti || []).length > 0 && (
                    <span className="text-[#4A5E51]"> · sottostanti: {m.compagnie_sottostanti.join(", ")}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-xs text-[#4A5E51] mt-3">
          L&apos;elenco completo aggiornato è disponibile su richiesta scritta a
          <a href="mailto:info@colucciassicuratori.com" className="text-[#163321] underline ml-1">
            info@colucciassicuratori.com
          </a>.
        </p>
      </section>

      {/* Sezione 3: Remunerazione */}
      <section className={CARD_CLASS + " mb-6"} data-testid="section-remunerazione">
        <h2 className="font-heading text-xl font-bold text-[#163321] flex items-center gap-2 mb-3">
          <FileText size={22} /> Sistema di Remunerazione
        </h2>
        <p className="text-sm text-[#4A5E51] mb-3">
          Ai sensi dell&apos;art. 61 Reg. IVASS 40/2018 e art. 121-bis CAP, il Cliente
          ha diritto di conoscere in via preventiva il compenso spettante
          all&apos;intermediario.
        </p>
        <ul className="text-sm text-[#4A5E51] space-y-2 list-disc pl-5">
          <li>
            <strong>Provvigione</strong> pagata dalla Compagnia sul premio (percentuale
            variabile 5-25% in funzione del ramo e prodotto).
          </li>
          <li>
            <strong>Commissioni di consulenza</strong> a carico del Cliente per attività
            di analisi complessa (esplicitate in preventivo separato prima della sottoscrizione).
          </li>
          <li>
            <strong>Fee di analisi</strong> una tantum per studi di rischio custom
            (D&amp;O, Cyber tailored).
          </li>
        </ul>
        <p className="text-sm text-[#4A5E51] mt-3">
          Il dettaglio della remunerazione applicata al singolo contratto è indicato
          nella documentazione precontrattuale specifica (IPID, DIP, DIP aggiuntivo)
          e nel preventivo formalizzato per il Cliente.
        </p>
      </section>

      {/* Sezione 4: Conflitti di interesse */}
      <section className={CARD_CLASS + " mb-6"} data-testid="section-conflitti">
        <h2 className="font-heading text-xl font-bold text-[#163321] flex items-center gap-2 mb-3">
          <AlertCircle size={22} /> Conflitti di interesse
        </h2>
        <div className="text-sm text-[#4A5E51] space-y-2">
          <p>Ai sensi dell&apos;art. 68 Reg. IVASS 40/2018, Colucci Assicuratori dichiara:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Non detiene partecipazioni azionarie in Compagnie mandanti</li>
            <li>Non è controllata direttamente o indirettamente da alcuna Compagnia mandante</li>
            <li>Non intrattiene rapporti di parentela con esponenti di Compagnie che configurino conflitto</li>
          </ul>
          <p className="mt-3">
            Qualora emerga un potenziale conflitto di interesse, l&apos;intermediario
            si impegna a informare il Cliente per iscritto prima della sottoscrizione
            e ad astenersi dal formalizzare il contratto senza consenso scritto informato.
          </p>
        </div>
      </section>

      {/* Sezione 5: Reclami */}
      <section className={CARD_CLASS + " mb-6"} data-testid="section-reclami">
        <h2 className="font-heading text-xl font-bold text-[#163321] flex items-center gap-2 mb-3">
          <Scale size={22} /> Procedura Reclami
        </h2>
        <div className="space-y-4 text-sm text-[#4A5E51]">
          <div>
            <p className="font-semibold text-[#163321] mb-1">1. Reclamo diretto (primo grado)</p>
            <p>
              Il Cliente può inviare reclamo scritto a{" "}
              <a href="mailto:reclami@colucciassicuratori.com" className="text-[#163321] underline">
                reclami@colucciassicuratori.com
              </a>. Risposta entro <strong>45 giorni</strong> lavorativi.
            </p>
          </div>
          <div>
            <p className="font-semibold text-[#163321] mb-1">2. Reclamo IVASS (secondo grado)</p>
            <p>
              Se la risposta risulta insoddisfacente o assente oltre 45 gg, il Cliente
              può rivolgersi a IVASS - Servizio Tutela del Consumatore.
            </p>
            <a href="https://www.ivass.it/consumatori/reclami/" target="_blank" rel="noopener noreferrer"
               className="text-xs text-[#163321] hover:underline inline-flex items-center gap-1 mt-1">
              Procedura ufficiale IVASS <ExternalLink size={11} />
            </a>
          </div>
          <div>
            <p className="font-semibold text-[#163321] mb-1">3. Arbitro Assicurativo</p>
            <p>Per dispute economiche fino a € 150.000, entro 60 giorni dalla risposta dell'agenzia.</p>
            <a href="https://www.acquirentiunici.it/" target="_blank" rel="noopener noreferrer"
               className="text-xs text-[#163321] hover:underline inline-flex items-center gap-1 mt-1">
              Arbitro Assicurativo <ExternalLink size={11} />
            </a>
          </div>
          <div>
            <p className="font-semibold text-[#163321] mb-1">4. Autorità Giudiziaria Ordinaria</p>
            <p>Sempre disponibile. Foro competente: Napoli (B2B) o foro del Consumatore (B2C).</p>
          </div>
        </div>
      </section>

      {/* Sezione 6: Privacy */}
      <section className={CARD_CLASS + " mb-6"} data-testid="section-privacy">
        <h2 className="font-heading text-xl font-bold text-[#163321] flex items-center gap-2 mb-3">
          <Users size={22} /> Trattamento dati personali
        </h2>
        <p className="text-sm text-[#4A5E51]">
          Il trattamento dei dati personali è disciplinato dall&apos;informativa privacy
          disponibile su{" "}
          <a href="/privacy" className="text-[#163321] underline">colucciassicuratori.com/privacy</a>{" "}
          e conforme al Reg. UE 2016/679 (GDPR).
        </p>
      </section>

      {/* Contatti finali */}
      <div className="bg-[#163321] rounded-2xl p-6 text-white">
        <h3 className="font-heading text-xl font-bold mb-2 flex items-center gap-2">
          <Mail size={22} /> Serve chiarimenti?
        </h3>
        <p className="text-sm text-white/80 mb-3">
          Per qualsiasi richiesta di trasparenza aggiuntiva, contattaci:
        </p>
        <div className="text-sm space-y-1">
          <p><strong>Email:</strong> <a href="mailto:info@colucciassicuratori.com" className="text-[#D9C49A] underline">info@colucciassicuratori.com</a></p>
          <p><strong>PEC reclami:</strong> <a href="mailto:reclami@colucciassicuratori.com" className="text-[#D9C49A] underline">reclami@colucciassicuratori.com</a></p>
          <p><strong>Sede legale:</strong> Via Armando Diaz 182/L, 80055 Portici (NA)</p>
        </div>
      </div>

      <p className="text-[10px] text-[#4A5E51] mt-8 text-center">
        Documento aggiornato il {new Date().toLocaleDateString("it-IT")} · Reg. IVASS 40/2018 · GDPR conformi
      </p>
    </div>
  );
}
